import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMediaQuery } from '@mui/material';
import { SERVICE_MENU_BASE } from '../config';
import { useAuth } from './AuthContext';
import { useBrand } from './BrandingContext';
import { useTenant } from './TenantContext';
import {
  applyDisplayNamePatch,
  MEGA_MENU_MOBILE_BREAKPOINT,
  MEGAMENU_NAME_REFRESH_INTERVAL,
  PATCHED_CLASS,
} from './util/megamenu';

interface MegaMenuContextType {
  isMenuInitialized: boolean;
  initializeMegaMenu: () => void;
}

export const MegaMenuContext = createContext<MegaMenuContextType | undefined>(
  undefined,
);

declare global {
  interface Window {
    DO_MEGA_MENU: boolean;
    InitMenu: (
      agentID: string,
      subscriberCookie: string,
      flag: string,
      canActAs: boolean,
      sorryServer: boolean,
    ) => void;
  }
}

const prefix = SERVICE_MENU_BASE;

export const MegaMenuProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isMenuInitialized, setIsMenuInitialized] = useState(false);
  const initializationInProgress = useRef(false);

  const { session } = useAuth();
  const { isAddEditHost } = useTenant();
  const { branding, applyCustomLogo } = useBrand();

  const isMobile = useMediaQuery(
    `only screen and (max-width: ${MEGA_MENU_MOBILE_BREAKPOINT - 1}px)`,
  );

  let userFirstName: string | null | undefined = null;
  let userLastName: string | null | undefined = null;
  if (session) {
    const { user } = session;
    const { agent } = user;
    userFirstName = agent?.MemberPreferredFirstName ?? user?.firstName;
    userLastName = agent?.MemberPreferredLastName ?? user?.lastName;
  }

  /**
   * 1. Prevents the original name from flashing by hiding target elements
   *    via CSS opacity as soon as they are added to the DOM.
   * 2. Fixes double-menu glitch on resize by conditionally hiding redundant
   *    menu containers.
   */
  const patchStyles = useMemo(
    () => `
    [name="usernameloggedin"],
    [name="usernameloggedinusername"],
    [name="usernameinitials"] {
      opacity: 0 !important;
    }
    [name="usernameloggedin"].${PATCHED_CLASS},
    [name="usernameloggedinusername"].${PATCHED_CLASS},
    [name="usernameinitials"].${PATCHED_CLASS} {
      opacity: 1 !important;
      transition: opacity 0.15s ease;
    }

    /* Fix double menu glitch on resize */
    @media only screen and (min-width: ${MEGA_MENU_MOBILE_BREAKPOINT}px) {
      #mega_menu_bottom > #mega_menu_right {
        display: none !important;
      }
    }
    @media only screen and (max-width: ${MEGA_MENU_MOBILE_BREAKPOINT - 1}px) {
      .bn-navbar-inverse > #mega_menu_right {
        display: none !important;
      }
    }
  `,
    [],
  );

  const loadMegaMenuResources = () => {
    return new Promise<void>((resolve) => {
      if (isAddEditHost() || branding?.customheader || !session) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `${prefix}/js/brightmlsmenu.js`;
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        resolve();
      };
    });
  };

  const cleanupMegaMenuResources = () => {
    if (isAddEditHost() || branding?.customheader) {
      return;
    }
    const script = document.querySelector(
      `script[src="${prefix}/assets/menu/js/brightmlsmenu.js"]`,
    );
    if (script) document.body.removeChild(script);
  };

  const initializeMegaMenu = async () => {
    if (
      !session?.user.memberMlsId ||
      isMenuInitialized ||
      initializationInProgress.current
    )
      return;

    if (isAddEditHost() || branding?.customheader) {
      setIsMenuInitialized(true);
      return;
    }

    initializationInProgress.current = true;

    try {
      await loadMegaMenuResources();

      const agentID = session?.user.memberMlsId || '';

      window.DO_MEGA_MENU = true;

      if (typeof window.InitMenu === 'function') {
        window.InitMenu(agentID, agentID, 'N', false, true);

        if (branding?.logo?.url && !branding.customheader) {
          applyCustomLogo(
            branding.logo.url,
            branding.logo.altText,
            branding.logo.width,
            branding.logo.height,
          );
        }
      }

      setIsMenuInitialized(true);
    } catch (error) {
      console.error('Failed to initialize mega menu:', error);
    } finally {
      initializationInProgress.current = false;
    }
  };

  /**
   * Keeps the displayed name patched for the lifetime of the menu.
   *
   * On resize the widget re-renders — the interval keeps running and re-patches
   * the fresh node automatically, also respecting the mobile breakpoint for
   * initials vs full name.
   */
  useEffect(() => {
    if (!userFirstName || !userLastName) return;
    const firstName = userFirstName;
    const lastName = userLastName;

    const intervalId = window.setInterval(() => {
      applyDisplayNamePatch(firstName, lastName, isMobile);
    }, MEGAMENU_NAME_REFRESH_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [userFirstName, userLastName, isMobile]);

  // Separate effect for menu resource cleanup on unmount.
  useEffect(() => {
    return () => {
      if (isMenuInitialized) {
        cleanupMegaMenuResources();
      }
    };
  }, [isMenuInitialized]);

  return (
    <MegaMenuContext.Provider value={{ isMenuInitialized, initializeMegaMenu }}>
      <style>{patchStyles}</style>
      {children}
    </MegaMenuContext.Provider>
  );
};

export const useMegaMenu = () => {
  const context = useContext(MegaMenuContext);
  if (context === undefined) {
    throw new Error('useMegaMenu must be used within a MegaMenuProvider');
  }
  return context;
};
